import React, { useState, useEffect } from 'react';
import './Transcript.css';

// VTT Cache - Component unmount olsa bile data korunur
const vttCache = new Map();

// VTT Parser functions (outside component to avoid re-creation)
const parseTimeStamp = (timestamp) => {
    // 00:00:00.000 formatından saniyeye çevir
    const parts = timestamp.split(':');
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const secondsParts = parts[2].split('.');
    const seconds = parseInt(secondsParts[0]);
    const milliseconds = parseInt(secondsParts[1] || 0);

    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
};

const parseVTT = (vttText) => {
    const lines = vttText.split('\n');
    const cues = [];
    let currentCue = null;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        // Time stamp line (00:00:00.000 --> 00:00:05.000)
        if (line.includes('-->')) {
            const [start, end] = line.split('-->').map(t => t.trim());
            currentCue = {
                startTime: parseTimeStamp(start),
                endTime: parseTimeStamp(end),
                text: ''
            };
        }
        // Text line
        else if (currentCue && line && !line.startsWith('WEBVTT') && !line.match(/^\d+$/)) {
            // VTT tag'lerini temizle (<v Speaker>, <c> vs.)
            const cleanText = line.replace(/<[^>]*>/g, '');
            currentCue.text += (currentCue.text ? ' ' : '') + cleanText;
        }
        // Empty line - cue tamamlandı
        else if (line === '' && currentCue && currentCue.text) {
            cues.push(currentCue);
            currentCue = null;
        }
    }

    // Son cue'yu ekle
    if (currentCue && currentCue.text) {
        cues.push(currentCue);
    }

    return cues;
};

const Transcript = ({ captionUrl, currentTime, onSeek }) => {
    const [transcriptData, setTranscriptData] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        if (!captionUrl) return;

        // Cache'de varsa direkt kullan
        if (vttCache.has(captionUrl)) {
            setTranscriptData(vttCache.get(captionUrl));
            return;
        }

        // Cache'de yoksa fetch et
        fetch(captionUrl)
            .then(response => response.text())
            .then(vttText => {
                const parsed = parseVTT(vttText);
                // Cache'e kaydet
                vttCache.set(captionUrl, parsed);
                setTranscriptData(parsed);
            })
            .catch(error => {
                console.error('Error loading caption:', error);
            });
    }, [captionUrl]);

    useEffect(() => {
        // Current time'a göre aktif transcript item'ı bul
        if (!transcriptData.length) return;

        const index = transcriptData.findIndex(item =>
            currentTime >= item.startTime && currentTime <= item.endTime
        );

        if (index !== -1) {
            setActiveIndex(index);
        }
    }, [currentTime, transcriptData]);

    // Arama fonksiyonu
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        const query = searchQuery.toLowerCase();
        const results = transcriptData
            .map((item, index) => ({ ...item, index }))
            .filter(item => item.text.toLowerCase().includes(query));

        setSearchResults(results);
    }, [searchQuery, transcriptData]);

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);

        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const handleTranscriptClick = (startTime, index) => {
        // Anında active state güncelle (visual feedback için)
        setActiveIndex(index);

        if (onSeek) {
            // StartTime'a 0.1 saniye ekle ki item'ın tam içine düşsün
            // Bu sayede video exact startTime'da değil, biraz içeride başlar
            // ve doğru caption görünür
            onSeek(startTime + 0.1);
        }
    };

    // Arama sonucunu highlight eden fonksiyon
    const highlightText = (text, query) => {
        if (!query.trim()) return text;

        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === query.toLowerCase()
                ? <mark key={index} className="transcript-highlight">{part}</mark>
                : part
        );
    };

    if (!transcriptData.length) {
        return (
            <div className="transcript-container">
                <div className="transcript-loading">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                    <p>Transkript yükleniyor...</p>
                </div>
            </div>
        );
    }

    // Arama yapıldığında sadece eşleşenleri göster, yoksa hepsini göster
    const hasSearchQuery = searchQuery.trim();
    const searchResultIndices = new Set(searchResults.map(r => r.index));

    return (
        <div className="transcript-container">
            {/* Search Input */}
            <div className="transcript-search">
                <i className="pi pi-search transcript-search-icon"></i>
                <input
                    type="text"
                    placeholder="Transkript içinde ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="transcript-search-input"
                />
                {searchQuery && (
                    <i
                        className="pi pi-times transcript-search-clear"
                        onClick={() => setSearchQuery('')}
                    ></i>
                )}
                {searchQuery && (
                    <span className="transcript-search-count">
                        {searchResults.length} sonuç
                    </span>
                )}
            </div>

            <div className="transcript-scroll-wrapper">
                <div className="transcript-list">
                    {hasSearchQuery && searchResults.length === 0 ? (
                        <div className="transcript-no-results">
                            <i className="pi pi-info-circle"></i>
                            <p>"{searchQuery}" için sonuç bulunamadı</p>
                        </div>
                    ) : (
                        transcriptData.map((item, index) => {
                            // Arama varsa ve bu item sonuçlarda değilse gizle
                            if (hasSearchQuery && !searchResultIndices.has(index)) {
                                return null;
                            }

                            return (
                                <div
                                    key={index}
                                    className={`transcript-item ${index === activeIndex ? 'active' : ''} ${hasSearchQuery && searchResultIndices.has(index) ? 'search-match' : ''}`}
                                    onClick={() => handleTranscriptClick(item.startTime, index)}
                                >
                                    <div className="transcript-time">
                                        {formatTime(item.startTime)}
                                    </div>
                                    <div className="transcript-text">
                                        {highlightText(item.text, searchQuery)}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default Transcript;
